<template>
  <section id="hero" class="relative pt-32 pb-12 lg:pt-40 lg:pb-20 overflow-hidden bg-slate-900">
    <!-- Background Slideshow -->
    <div class="absolute inset-0 z-0 overflow-hidden">
      <transition-group 
        name="fade" 
        tag="div" 
        class="relative w-full h-full"
      >
        <div 
          v-for="(image, index) in projectImages" 
          :key="image"
          v-show="currentIndex === index"
          class="absolute inset-0 w-full h-full"
        >
          <img 
            :src="getImageUrl(image)" 
            class="w-full h-full object-cover opacity-[0.15] scale-105"
            loading="lazy"
          />
        </div>
      </transition-group>
      
      <!-- Fallback Gradient if no images -->
      <div v-if="projectImages.length === 0" class="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-amber-900/40 via-slate-900/80 to-slate-900"></div>
      
      <!-- Elegant Overlays -->
      <div class="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-transparent to-slate-900"></div>
      <div class="absolute inset-0 bg-slate-900/40"></div>
      <div class="absolute top-0 right-0 w-1/2 h-full bg-amber-500/5 blur-[120px] rounded-full transform translate-x-1/2 -translate-y-1/2"></div>
      
      <!-- Architectural Grid Overlay -->
      <div class="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTAgMGg0MHY0MEgwaC00MHoiIGZpbGw9Im5vbmUiLz48cGF0aCBkPSJNMCAwaDQwdjQwSDB6IiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wMykiIHN0cm9rZS13aWR0aD0iMSIvPjwvc3ZnPg==')] z-0"></div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div class="lg:w-2/3 mb-16 lg:mb-20">
        <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500 text-sm font-semibold mb-6">
          <span class="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
          Building the Future
        </div>
        
        <h1 class="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white tracking-tight leading-tight mb-6">
          The Power <br class="hidden md:block"/>of <span class="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Us</span>
        </h1>
        
        <p class="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl leading-relaxed">
          At EM CONTRACTING CORP, we build more than spaces—we build trust and deliver confidence. Our clients rely on us to move fast, stay focused, and lead with clarity.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4">
          <a href="#contact" class="inline-flex justify-center items-center px-8 py-4 text-base font-bold rounded-md text-slate-900 bg-amber-500 hover:bg-amber-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-amber-500 transition-all duration-300 transform hover:-translate-y-1 shadow-lg shadow-amber-500/25">
            Start Your Project
            <svg class="ml-2 -mr-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
          </a>
          <a href="#services" class="inline-flex justify-center items-center px-8 py-4 text-base font-medium rounded-md text-white bg-slate-800 hover:bg-slate-700 border border-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-slate-500 transition-all duration-300">
            Explore Services
          </a>
        </div>
      </div>
      
      <!-- Stats / Trust Indicators -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-white/5 pt-8">
        <div>
          <div class="text-3xl font-bold text-white mb-1">{{ stats.stats_years_experience }}</div>
          <div class="text-sm text-gray-400 font-medium tracking-wide uppercase">Years Experience</div>
        </div>
        <div>
          <div class="text-3xl font-bold text-white mb-1">{{ stats.stats_projects_completed }}</div>
          <div class="text-sm text-gray-400 font-medium tracking-wide uppercase">Projects Completed</div>
        </div>
        <div>
          <div class="text-3xl font-bold text-white mb-1">{{ stats.stats_value_delivered }}</div>
          <div class="text-sm text-gray-400 font-medium tracking-wide uppercase">Value Delivered</div>
        </div>
        <div>
          <div class="text-3xl font-bold text-white mb-1">{{ stats.stats_safety_commitment }}</div>
          <div class="text-sm text-gray-400 font-medium tracking-wide uppercase">Safety Commitment</div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import projectService from '@/services/projectService'

export default {
  name: 'HeroSection',
  data() {
    return {
      projectImages: [],
      currentIndex: 0,
      slideshowInterval: null
    }
  },
  computed: {
    ...mapGetters('settings', ['stats']),
    apiUrl() {
      return process.env.VUE_APP_API_URL ? process.env.VUE_APP_API_URL.replace('/api', '') : 'https://em-admin.test';
    }
  },
  async created() {
    try {
      const response = await projectService.getProjects();
      const activeProjects = response.data.filter(p => p.is_active && p.cover_image_path);
      this.projectImages = activeProjects.slice(0, 5).map(p => p.cover_image_path);
      
      if (this.projectImages.length > 1) {
        this.startSlideshow();
      }
    } catch (error) {
      console.error('Error fetching projects for hero background:', error);
    }
  },
  beforeDestroy() {
    if (this.slideshowInterval) {
      clearInterval(this.slideshowInterval);
    }
  },
  methods: {
    getImageUrl(path) {
      if (!path) return '';
      if (path.startsWith('http')) return path;
      return `${this.apiUrl}/${path}`;
    },
    startSlideshow() {
      this.slideshowInterval = setInterval(() => {
        this.currentIndex = (this.currentIndex + 1) % this.projectImages.length;
      }, 5000); // Change every 5 seconds for a smooth feel
    }
  }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 1.5s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
